import React, { useState, Suspense, lazy } from 'react'
import { Radio, Play, X } from 'lucide-react'
import { genId } from '../lib/id'
import { useLessonDoc, lessonToLiveQuestions } from '../lib/lessonLive'
import type { LiveSessionCard } from '../types/LessonDoc'
import type { CardDef, CardEditorProps, CardPreviewProps } from './types'

// Nạp lazy (TeacherSession kéo theo chart.js/mammoth/katex) để không làm nặng trang soạn bài.
const TeacherSession = lazy(() => import('../../../components/interactive/TeacherSession'))

const Editor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const c = card as LiveSessionCard
  return (
    <div className="space-y-2">
      <div className="p-3 rounded-lg bg-fuchsia-50 border border-fuchsia-100 text-fuchsia-700 text-sm">
        <b>Phiên tương tác trực tiếp</b>: trắc nghiệm, đám mây từ, trả lời ngắn — kết quả hiển thị <b>realtime</b>. Học sinh quét QR / nhập mã để tham gia.
      </div>
      <label className="text-xs font-bold text-gray-500 uppercase">Ghi chú hoạt động (tuỳ chọn)</label>
      <textarea
        className="w-full min-h-[60px] p-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-fuchsia-200 outline-none"
        value={c.note || ''}
        onChange={(e) => onChange({ note: e.target.value })}
        placeholder="VD: Khởi động bằng 3 câu trắc nghiệm về dao động điều hoà…"
      />
    </div>
  )
}

const Preview: React.FC<CardPreviewProps> = ({ card }) => {
  const c = card as LiveSessionCard
  const [open, setOpen] = useState(false)
  // Lấy bài giảng hiện tại từ context (builder preview / trang xem bài) →
  // nạp sẵn các câu hỏi tương tác THẬT của bài vào phiên trực tiếp.
  const lessonDoc = useLessonDoc()
  const liveQuestions = lessonToLiveQuestions(lessonDoc)
  return (
    <div className="rounded-xl border border-fuchsia-200 bg-fuchsia-50/40 p-5 text-center">
      <div className="w-12 h-12 mx-auto rounded-full bg-fuchsia-100 text-fuchsia-600 flex items-center justify-center mb-2"><Radio className="w-6 h-6" /></div>
      <h4 className="font-bold text-fuchsia-800">{c.title || 'Real-time Survey'}</h4>
      <p className="text-sm text-gray-500 mb-1">Trắc nghiệm · Đám mây từ · Trả lời ngắn — kết quả realtime.</p>
      {c.note && <p className="text-xs text-gray-400 mb-2">{c.note}</p>}
      <button onClick={() => setOpen(true)} className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-fuchsia-600 text-white font-bold hover:bg-fuchsia-700">
        <Play className="w-4 h-4" /> Mở phiên trực tiếp
      </button>
      <p className="text-[11px] text-gray-400 mt-2">
        Khi mở, hệ thống tạo <b>mã + QR</b>; học sinh vào trang <b>/live</b> để tham gia.
        {liveQuestions.length > 0 && <> Tự nạp sẵn <b>{liveQuestions.length} câu hỏi</b> từ bài giảng này.</>}
      </p>
      {open && (
        <div className="fixed inset-0 z-[200] bg-white overflow-auto text-left">
          <button onClick={() => setOpen(false)} className="fixed top-3 right-3 z-[210] inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-800 text-white text-sm font-bold hover:bg-gray-900">
            <X className="w-4 h-4" /> Đóng
          </button>
          <Suspense fallback={<div className="p-10 text-center text-gray-500">Đang tải phiên trực tiếp…</div>}>
            <TeacherSession initialQuestions={liveQuestions} />
          </Suspense>
        </div>
      )}
    </div>
  )
}

export const liveSessionCardDef: CardDef = {
  type: 'live_session',
  label: 'Real-time Survey',
  description: 'Tương tác lớp realtime: trắc nghiệm / đám mây từ / trả lời ngắn',
  icon: Radio,
  color: 'bg-fuchsia-50 text-fuchsia-600',
  createDefault: (): LiveSessionCard => ({ id: genId(), type: 'live_session' }),
  Editor,
  Preview,
}
